import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Send, Loader2, Save, Lightbulb, Copy, ClipboardList } from "lucide-react";
import { modeToProjectType } from "./guideModes";
import RichText from "./RichText";

const STATUS_FIELDS = [
  { key: "products_resources", label: "Products & resources", hint: "One per line. e.g. Starter Kit, paid (active); Free checklist, lead magnet (active)", placeholder: "Name, free/paid, draft/active/paused/retired" },
  { key: "content_status", label: "Content", hint: "One per line. e.g. Launch blog post, published", placeholder: "Title, drafted/scheduled/published" },
  { key: "offers_campaigns", label: "Offers & campaigns", hint: "One per line", placeholder: "Name, planned/active/completed" },
  { key: "platforms_features", label: "Platforms & features", hint: "One per line", placeholder: "Name, planned/active/paused" },
  { key: "notes", label: "Other current notes", hint: "Anything else about the present state", placeholder: "" },
];

export default function GuideChat({ mode, conversationId, projectId, onConversationCreated, onBack, onSaveNeedsProject, onSavedItem }) {
  const Icon = mode.icon;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [convoId, setConvoId] = useState(conversationId || null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusDoc, setStatusDoc] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const scrollRef = useRef(null);

  const openStatus = async () => {
    setStatusOpen(true);
    try {
      const r = await api.get("/business-status");
      setStatusDoc(r.data || {});
    } catch (e) {
      toast.error("Could not load your current business status.");
      setStatusDoc({});
    }
  };

  const saveStatus = async () => {
    setSavingStatus(true);
    try {
      const payload = {};
      STATUS_FIELDS.forEach((f) => { payload[f.key] = statusDoc?.[f.key] || ""; });
      await api.put("/business-status", payload);
      toast.success("Current business status saved.");
      setStatusOpen(false);
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally { setSavingStatus(false); }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      if (conversationId) {
        try {
          const r = await api.get(`/guide/conversations/${conversationId}`);
          if (active) { setMessages(r.data.messages || []); setConvoId(conversationId); }
        } catch (e) { toast.error("Could not load that conversation."); }
      } else {
        setMessages([]); setConvoId(null);
      }
    })();
    return () => { active = false; };
  }, [conversationId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const r = await api.post("/guide/chat", { conversation_id: convoId, mode: mode.key, message: msg, project_id: projectId || null });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
      if (!convoId && r.data.conversation_id) {
        setConvoId(r.data.conversation_id);
        onConversationCreated?.(r.data.conversation_id, r.data.title);
      }
    } catch (e) {
      toast.error("The AI Business Guide is unavailable right now. Please try again in a moment.");
    } finally { setSending(false); }
  };

  const saveProject = async (content) => {
    if (!projectId && onSaveNeedsProject) { onSaveNeedsProject(content, convoId); return; }
    try {
      await api.post("/guide/items", {
        project_id: projectId || null,
        kind: modeToProjectType[mode.key] || "draft",
        title: content.trim().split("\n").find((l) => l.trim())?.replace(/[*#>]/g, "").trim().slice(0, 80) || "Saved draft",
        content, mode: mode.key, source_conversation_id: convoId,
      });
      toast.success(projectId ? "Saved to this project." : "Saved.");
      onSavedItem?.();
    } catch (e) { toast.error("Could not save."); }
  };

  const toIdeaVault = async (content) => {
    try {
      await api.post("/ideas", {
        title: content.trim().split("\n").find((l) => l.trim())?.replace(/\*/g, "").slice(0, 80) || "Idea from the Guide",
        category: "Blog ideas", notes: content, status: "New",
      });
      toast.success("Sent to Idea Vault.");
    } catch (e) { toast.error("Could not send to Idea Vault."); }
  };

  const copyText = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Copied to clipboard."); }
    catch (e) { toast.error("Could not copy."); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-11rem)] min-h-[520px]" data-testid="guide-chat">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} data-testid="guide-back" className="p-2 rounded-full hover:bg-muted text-muted-foreground transition-colors" title="Back to Guide">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl leading-tight truncate">{mode.label}</h1>
          <p className="text-xs text-muted-foreground truncate">{mode.blurb}</p>
        </div>
        {mode.key === "business" && (
          <Button onClick={openStatus} variant="outline" size="sm" data-testid="edit-business-status-btn"
            className="ml-auto shrink-0 rounded-full border-border gap-1.5">
            <ClipboardList className="h-4 w-4" /> Current status
          </Button>
        )}
      </div>

      <div ref={scrollRef} data-testid="guide-messages" className="flex-1 overflow-y-auto rounded-3xl border border-border bg-white/50 p-4 sm:p-6 space-y-4">
        {messages.length === 0 && !sending && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="h-14 w-14 rounded-3xl bg-primary/10 flex items-center justify-center mb-4">
              <Icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
            </div>
            <p className="font-heading text-xl mb-2">How can I help you today?</p>
            <p className="text-sm text-muted-foreground max-w-md mb-6">I know your brand voice, themes and workspace. Ask me anything, or start with one of these:</p>
            <div className="flex flex-col gap-2 w-full max-w-md">
              {mode.starters.map((s, i) => (
                <button key={i} data-testid={`guide-starter-${i}`} onClick={() => send(s)}
                  className="text-left text-sm px-4 py-3 rounded-2xl bg-muted/60 hover:bg-muted text-foreground/80 hover:text-foreground transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-3xl px-4 py-3 text-sm leading-relaxed ${
              m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted/70 text-foreground"
            }`}>
              {m.role === "user"
                ? <p className="whitespace-pre-wrap">{m.content}</p>
                : <RichText text={m.content} />}
              {m.role === "assistant" && (
                <div className="flex items-center gap-1 mt-3 pt-2 border-t border-border/60">
                  <button onClick={() => saveProject(m.content)} data-testid={`guide-save-reply-${messages.slice(0, i).filter((message) => message.role === "assistant").length}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-full">
                    <Save className="h-3.5 w-3.5" /> Save
                  </button>
                  <button onClick={() => toIdeaVault(m.content)} data-testid={`guide-to-idea-${i}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-full">
                    <Lightbulb className="h-3.5 w-3.5" /> Idea Vault
                  </button>
                  <button onClick={() => copyText(m.content)} data-testid={`guide-copy-reply-${i}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-full">
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="rounded-3xl px-4 py-3 bg-muted/70 text-muted-foreground text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Thinking gently…
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-end gap-2">
        <Textarea
          data-testid="guide-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Message ${mode.label}…`}
          className="rounded-2xl min-h-[52px] max-h-40 bg-white border-border resize-none"
        />
        <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="guide-send"
          className="rounded-2xl h-[52px] w-[52px] p-0 bg-primary hover:bg-primary/90 shrink-0">
          {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </Button>
      </div>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="business-status-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Current Business Status</DialogTitle>
            <DialogDescription className="text-sm text-foreground/70 leading-relaxed">
              The authoritative source for what's live right now. Business Insights answers present-state questions from this, not from drafts, plans or the Knowledge Library.
            </DialogDescription>
          </DialogHeader>
          {statusDoc === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-4">
              {STATUS_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label>{f.label}</Label>
                  <p className="text-xs text-muted-foreground">{f.hint}</p>
                  <Textarea
                    data-testid={`business-status-${f.key}`}
                    value={statusDoc[f.key] || ""}
                    onChange={(e) => setStatusDoc((d) => ({ ...d, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="rounded-xl min-h-[80px]"
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="flex-row justify-end gap-2">
            <Button variant="outline" onClick={() => setStatusOpen(false)} data-testid="business-status-cancel" className="rounded-full border-border">Cancel</Button>
            <Button onClick={saveStatus} disabled={savingStatus || statusDoc === null} data-testid="business-status-save" className="rounded-full bg-primary hover:bg-primary/90 gap-1.5">
              {savingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
